import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Palette, Pencil, Trash2 } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import WorkspaceChip from './WorkspaceChip';

/**
 * One row of Manage workspaces: grip, chip, name — an inline modal-input while
 * renaming (F2, double-click or the pencil; Enter saves, Escape cancels) — and
 * Rename / Icon & colour / Delete. Alt+Up / Alt+Down move it.
 */
export default function ManageWorkspaceRow({ ws, renaming, onStartRename, onRename, onCancelRename, onEdit, onDelete, onMove, drag, ns = 'docSidebar.workspaces' }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(ws.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!renaming) return;
    setDraft(ws.name);
    requestAnimationFrame(() => inputRef.current?.select());
  }, [renaming, ws.name]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== ws.name) onRename(next);
    else onCancelRename();
  };

  const onKeyDown = (e) => {
    if (renaming || e.target !== e.currentTarget) return;
    if (e.key === 'F2') {
      e.preventDefault();
      onStartRename();
    } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      onMove(e.key === 'ArrowUp' ? -1 : 1);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      onDelete();
    }
  };

  return (
    <li
      className="doc-ws-row"
      tabIndex={0}
      data-testid={`doc-ws-row-${ws.id}`}
      data-drop={drag.marker}
      aria-label={ws.name}
      onKeyDown={onKeyDown}
      {...drag.props}
    >
      <span className="doc-ws-grip" aria-hidden>
        <GripVertical size={14} />
      </span>
      <WorkspaceChip choice={{ kind: 'workspace', icon: ws.icon, color: ws.color }} />
      {renaming ? (
        <input
          ref={inputRef}
          className="modal-input doc-ws-rename"
          value={draft}
          maxLength={60}
          aria-label={t(`${ns}.nameLabel`)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancelRename();
            }
          }}
        />
      ) : (
        <span className="doc-ws-name" onDoubleClick={onStartRename}>
          {ws.name}
        </span>
      )}
      <span className="doc-ws-actions">
        <Tooltip content={t(`${ns}.rename`)} side="bottom">
          <button type="button" className="doc-ws-btn" aria-label={t(`${ns}.rename`)} onClick={onStartRename}>
            <Pencil size={14} />
          </button>
        </Tooltip>
        <Tooltip content={t(`${ns}.edit`)} side="bottom">
          <button type="button" className="doc-ws-btn" aria-label={t(`${ns}.edit`)} onClick={onEdit}>
            <Palette size={14} />
          </button>
        </Tooltip>
        <Tooltip content={t(`${ns}.delete`)} side="bottom">
          <button
            type="button"
            className="doc-ws-btn doc-ws-btn--danger"
            aria-label={t(`${ns}.delete`)}
            onClick={onDelete}
          >
            <Trash2 size={14} />
          </button>
        </Tooltip>
      </span>
    </li>
  );
}
